// Who is signed in (D-LAUNCH2).
//
// Cloudflare Access (Google Workspace, @mrappdv.com only) sits in front of the
// Pages site. Every Access-protected hostname answers
// /cdn-cgi/access/get-identity with the signed-in user's JSON, same-origin, so
// the Access cookie rides along. Anything else — local dev, Access not yet
// switched on, a Cloudflare hiccup — returns null and the app falls back to
// the typed-name gate. The identity check must never lock a tech out.
//
// The worker does NOT verify this identity yet (Phase B in
// CLOUDFLARE_ACCESS.md); it trusts X-Tech-Email as it trusts X-Tech-Name.

import { readLocal, writeLocal, removeLocal, KEYS, TECH_NAME_EVENT } from './storage.js';

export const IDENTITY_URL = '/cdn-cgi/access/get-identity';

// Set the instant a tech taps Switch tech, before storage is even cleared. A
// get-identity request from before the tap can resolve after switchTech has
// already cleared storage but before the logout navigation actually unloads
// the page — without this guard, applyIdentity would silently write the
// outgoing tech's identity right back during that window.
let signingOut = false;

/** Test-only: clears the signingOut latch so later tests start clean. */
export function _resetSigningOutForTests() {
  signingOut = false;
}

/** 'mike.rodriguez@x' → 'Mike Rodriguez'. Used when Google sends no name. */
export function nameFromEmail(email) {
  const local = String(email || '').split('@')[0];
  return local
    .split(/[._-]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/** { email, name } from get-identity's JSON, or null if it isn't a signed-in user. */
export function parseIdentity(json) {
  if (!json || typeof json !== 'object') return null;
  const email = String(json.email || '').trim().toLowerCase();
  if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) return null;
  const name = String(json.name || '').trim() || nameFromEmail(email);
  return { email, name };
}

/** Header values must be ByteStrings; fetch() throws on anything else. */
export function asciiHeader(value) {
  return String(value || '').replace(/[^\x20-\x7E]/g, '').trim();
}

export function firstName(name) {
  return String(name || '').trim().split(/\s+/)[0] || '';
}

/** Ask Access who is signed in. Never throws; null means "use the fallback". */
export async function loadIdentity({ fetchImpl = fetch, timeoutMs = 4000 } = {}) {
  let timer;
  try {
    const controller = new AbortController();
    timer = setTimeout(() => controller.abort(), timeoutMs);
    const res = await fetchImpl(IDENTITY_URL, { credentials: 'same-origin', signal: controller.signal });
    if (!res || !res.ok) return null;
    const json = await res.json().catch(() => null);
    return parseIdentity(json);
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Store the signed-in identity. The Google name replaces any typed name, and a
 * different email (a shared iPad changed hands) replaces the whole identity.
 * Returns true when something changed, and tells the app to re-read it.
 */
export function applyIdentity(identity) {
  if (signingOut) return false;
  const prevEmail = readLocal(KEYS.techEmail, '');
  const prevName = readLocal(KEYS.techName, '');
  if (prevEmail === identity.email && prevName === identity.name) return false;
  writeLocal(KEYS.techEmail, identity.email);
  writeLocal(KEYS.techName, identity.name);
  if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
    window.dispatchEvent(new Event(TECH_NAME_EVENT));
  }
  return true;
}

/**
 * Drop the signed-in email, leaving any typed name in place. Called when a
 * tech types a name on the fallback gate, so a previous person's Access
 * email never rides along with a new typed name on a shared iPad.
 */
export function clearIdentity() {
  removeLocal(KEYS.techEmail);
  if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
    window.dispatchEvent(new Event(TECH_NAME_EVENT));
  }
}

/**
 * Switch to another tech on a shared iPad (Help → Switch tech). Signing out
 * of Google alone does NOT end the Cloudflare Access session — the
 * CF_Authorization cookie on this domain plus the team-domain cookie live for
 * ~24h — so the next tech's work would still log under the previous tech
 * unless we also hit Access's own logout endpoint. The typed-name fallback
 * has no such cookie, so a plain trip back to '/' (the name gate) is enough —
 * but its welcome-seen flag is cleared too, so the next typed-name tech gets
 * the first-run cards instead of inheriting the outgoing tech's "seen" state.
 *
 * Also drops the outgoing tech's active scan (KEYS.lastScan), so the next
 * tech on a shared iPad lands on a blank Scan Tag screen instead of the
 * previous tech's in-progress job. The scan itself and any troubleshooting
 * turns are untouched in D1 and still reachable from History; the per-scan
 * `troubleshootSession:<lookupId>` local key is left alone too — it's keyed
 * by lookup id, not by tech, so it's harmless to leave behind.
 */
export function switchTech({ navigate = (u) => { window.location.href = u; } } = {}) {
  signingOut = true;
  const hadEmail = !!readLocal(KEYS.techEmail, '');
  removeLocal(KEYS.techEmail);
  removeLocal(KEYS.techName);
  removeLocal(KEYS.lastScan);
  if (!hadEmail) removeLocal(`${KEYS.welcomeSeenPrefix}local`);
  navigate(hadEmail ? '/cdn-cgi/access/logout' : '/');
}
